/**
 * CTC Lazy Loading Library
 * Centralized lazy loading utilities for all CTC sections
 * Provides image lazy loading, API lazy loading, and section initialization
 */

(function(window) {
  'use strict';

  // Lazy loading class
  function CTCLazyLoad() {
    this.imageObserver = null;
    this.sectionObserver = null;
    this.isIntersectionObserverSupported = 'IntersectionObserver' in window;
    this.loadedSections = new Set();
    this.pendingImages = new Map();
    
    this.init();
  }

  CTCLazyLoad.prototype = {
    init: function() {
      this.setupImageObserver();
      this.setupSectionObserver();
      this.bindEvents();
    },

    /**
     * Setup Intersection Observer for lazy loading images
     */
    setupImageObserver: function() {
      if (!this.isIntersectionObserverSupported) {
        this.fallbackImageLoading();
        return;
      }

      var self = this;
      this.imageObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            self.loadImage(entry.target);
            self.imageObserver.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '50px 0px',
        threshold: 0.01
      });
    },

    /**
     * Setup Intersection Observer for lazy loading sections
     */
    setupSectionObserver: function() {
      if (!this.isIntersectionObserverSupported) {
        this.fallbackSectionLoading();
        return;
      }

      var self = this;
      this.sectionObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            self.loadSection(entry.target);
            self.sectionObserver.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '100px 0px',
        threshold: 0.01
      });
    },

    /**
     * Load image with lazy loading
     */
    loadImage: function(img) {
      var self = this;
      
      // Handle different image types
      if (img.tagName === 'IMG') {
        this.loadImageElement(img);
      } else if (img.hasAttribute('data-bg-src')) {
        this.loadBackgroundImage(img);
      }
    },

    /**
     * Load IMG element
     */
    loadImageElement: function(img) {
      var src = img.getAttribute('data-src');
      var srcset = img.getAttribute('data-srcset');
      
      if (!src) return;

      // Add loading class
      img.classList.add('ctc-image-loading');
      
      // Create new image to preload
      var newImg = new Image();
      
      newImg.onload = function() {
        img.src = src;
        if (srcset) {
          img.srcset = srcset;
        }
        img.classList.remove('ctc-image-loading');
        img.classList.add('ctc-image-loaded');
        
        // Trigger custom event
        img.dispatchEvent(new CustomEvent('ctc:imageLoaded', { bubbles: true }));
      };
      
      newImg.onerror = function() {
        img.classList.remove('ctc-image-loading');
        img.classList.add('ctc-image-error');
      };
      
      newImg.src = src;
      if (srcset) {
        newImg.srcset = srcset;
      }
    },

    /**
     * Load background image
     */
    loadBackgroundImage: function(element) {
      var src = element.getAttribute('data-bg-src');
      if (!src) return;

      element.classList.add('ctc-bg-loading');
      
      var img = new Image();
      img.onload = function() {
        element.style.backgroundImage = 'url(' + src + ')';
        element.classList.remove('ctc-bg-loading');
        element.classList.add('ctc-bg-loaded');
        
        // Trigger custom event
        element.dispatchEvent(new CustomEvent('ctc:bgLoaded', { bubbles: true }));
      };
      
      img.onerror = function() {
        element.classList.remove('ctc-bg-loading');
        element.classList.add('ctc-bg-error');
      };
      
      img.src = src;
    },

    /**
     * Load section (API calls, sliders, etc.)
     */
    loadSection: function(section) {
      var sectionId = section.getAttribute('data-section-id') || section.id;
      
      if (this.loadedSections.has(sectionId)) {
        return;
      }
      
      this.loadedSections.add(sectionId);
      section.classList.add('ctc-section-loading');
      
      // Handle different section types
      var sectionType = section.getAttribute('data-lazy-type');
      
      switch(sectionType) {
        case 'api':
          this.loadApiSection(section);
          break;
        case 'slider':
          this.loadSliderSection(section);
          break;
        case 'widget':
          this.loadWidgetSection(section);
          break;
        default:
          this.loadDefaultSection(section);
      }
    },

    /**
     * Load API-driven sections (trucks, Instagram)
     */
    loadApiSection: function(section) {
      var self = this;
      var apiHandler = section.getAttribute('data-api-handler');
      
      if (apiHandler && window[apiHandler]) {
        try {
          window[apiHandler](section);
          section.classList.remove('ctc-section-loading');
          section.classList.add('ctc-section-loaded');
        } catch(e) {
          console.error('Error loading API section:', e);
          section.classList.add('ctc-section-error');
        }
      }
    },

    /**
     * Load slider sections
     */
    loadSliderSection: function(section) {
      var self = this;
      var sliderHandler = section.getAttribute('data-slider-handler');
      
      // Wait for dependencies (jQuery, Slick)
      this.waitForDependencies(['jQuery', 'slick'], function() {
        try {
          if (sliderHandler && window[sliderHandler]) {
            window[sliderHandler](section);
          } else {
            self.initializeGenericSlider(section);
          }
          section.classList.remove('ctc-section-loading');
          section.classList.add('ctc-section-loaded');
        } catch(e) {
          console.error('Error loading slider section:', e);
          section.classList.add('ctc-section-error');
        }
      });
    },

    /**
     * Load widget sections (Trustpilot, etc.)
     */
    loadWidgetSection: function(section) {
      var widgetHandler = section.getAttribute('data-widget-handler');
      
      if (widgetHandler && window[widgetHandler]) {
        try {
          window[widgetHandler](section);
          section.classList.remove('ctc-section-loading');
          section.classList.add('ctc-section-loaded');
        } catch(e) {
          console.error('Error loading widget section:', e);
          section.classList.add('ctc-section-error');
        }
      }
    },

    /**
     * Load default sections
     */
    loadDefaultSection: function(section) {
      section.classList.remove('ctc-section-loading');
      section.classList.add('ctc-section-loaded');
      
      // Trigger section loaded event
      section.dispatchEvent(new CustomEvent('ctc:sectionLoaded', { bubbles: true }));
    },

    /**
     * Wait for dependencies to load
     */
    waitForDependencies: function(deps, callback) {
      var self = this;
      var checkInterval = 50;
      var maxAttempts = 100;
      var attempts = 0;
      
      function check() {
        attempts++;
        var allLoaded = deps.every(function(dep) {
          if (dep === 'jQuery') {
            return window.jQuery && window.jQuery.fn;
          }
          if (dep === 'slick') {
            return window.jQuery && window.jQuery.fn && window.jQuery.fn.slick;
          }
          return window[dep];
        });
        
        if (allLoaded) {
          callback();
        } else if (attempts < maxAttempts) {
          setTimeout(check, checkInterval);
        } else {
          console.warn('Dependencies not loaded after maximum attempts:', deps);
        }
      }
      
      check();
    },

    /**
     * Initialize generic slider
     */
    initializeGenericSlider: function(section) {
      var $slider = window.jQuery(section).find('[data-slider="true"]');
      if ($slider.length) {
        $slider.slick({
          // Default slider settings
          slidesToShow: 4,
          slidesToScroll: 1,
          infinite: true,
          arrows: false,
          dots: true,
          responsive: [
            {
              breakpoint: 1024,
              settings: {
                slidesToShow: 2
              }
            },
            {
              breakpoint: 480,
              settings: {
                slidesToShow: 1
              }
            }
          ]
        });
      }
    },

    /**
     * Fallback for browsers without Intersection Observer
     */
    fallbackImageLoading: function() {
      var images = document.querySelectorAll('[data-src], [data-bg-src]');
      for (var i = 0; i < images.length; i++) {
        this.loadImage(images[i]);
      }
    },

    /**
     * Fallback section loading
     */
    fallbackSectionLoading: function() {
      var sections = document.querySelectorAll('[data-lazy-load="true"]');
      for (var i = 0; i < sections.length; i++) {
        this.loadSection(sections[i]);
      }
    },

    /**
     * Observe images for lazy loading
     */
    observeImages: function(container) {
      if (!container) container = document;
      
      var images = container.querySelectorAll('[data-src], [data-bg-src]');
      
      for (var i = 0; i < images.length; i++) {
        if (this.imageObserver) {
          this.imageObserver.observe(images[i]);
        }
      }
    },

    /**
     * Observe sections for lazy loading
     */
    observeSections: function(container) {
      if (!container) container = document;
      
      var sections = container.querySelectorAll('[data-lazy-load="true"]');
      
      for (var i = 0; i < sections.length; i++) {
        if (this.sectionObserver) {
          this.sectionObserver.observe(sections[i]);
        }
      }
    },

    /**
     * Bind events
     */
    bindEvents: function() {
      var self = this;
      
      // Handle Shopify theme editor events
      document.addEventListener('shopify:section:load', function(e) {
        setTimeout(function() {
          var container = document.getElementById('shopify-section-' + e.detail.sectionId);
          if (container) {
            self.observeImages(container);
            self.observeSections(container);
          }
        }, 100);
      });
      
      // Handle page visibility changes
      document.addEventListener('visibilitychange', function() {
        if (!document.hidden) {
          // Re-observe when page becomes visible
          self.observeImages();
          self.observeSections();
        }
      });
    },

    /**
     * Public method to refresh observers
     */
    refresh: function() {
      this.observeImages();
      this.observeSections();
    },

    /**
     * Public method to load all images immediately
     */
    loadAllImages: function() {
      var images = document.querySelectorAll('[data-src], [data-bg-src]');
      for (var i = 0; i < images.length; i++) {
        this.loadImage(images[i]);
      }
    },

    /**
     * Public method to load all sections immediately
     */
    loadAllSections: function() {
      var sections = document.querySelectorAll('[data-lazy-load="true"]');
      for (var i = 0; i < sections.length; i++) {
        this.loadSection(sections[i]);
      }
    }
  };

  // Initialize lazy loading when DOM is ready
  function initLazyLoad() {
    window.ctcLazyLoad = new CTCLazyLoad();
    window.ctcLazyLoad.observeImages();
    window.ctcLazyLoad.observeSections();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLazyLoad);
  } else {
    initLazyLoad();
  }

  // CSS for loading states
  var style = document.createElement('style');
  style.textContent = `
    /* CTC Lazy Loading Styles */
    .ctc-image-loading {
      opacity: 0.3;
      transition: opacity 0.3s ease;
    }
    
    .ctc-image-loaded {
      opacity: 1;
    }
    
    .ctc-image-error {
      opacity: 0.5;
      background-color: #f5f5f5;
    }
    
    .ctc-bg-loading {
      background-color: #f5f5f5;
      background-image: none !important;
    }
    
    .ctc-bg-loaded {
      transition: background-image 0.3s ease;
    }
    
    .ctc-section-loading {
      position: relative;
      min-height: 100px;
    }
    
    .ctc-section-loading::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 40px;
      height: 40px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid #b46a1f;
      border-radius: 50%;
      animation: ctc-spin 1s linear infinite;
    }
    
    @keyframes ctc-spin {
      0% { transform: translate(-50%, -50%) rotate(0deg); }
      100% { transform: translate(-50%, -50%) rotate(360deg); }
    }
    
    .ctc-section-loaded::before {
      display: none;
    }
    
    .ctc-section-error {
      background-color: #ffebee;
      border: 1px solid #ffcdd2;
    }
    
    /* Lazy load placeholder for images */
    [data-src], [data-bg-src] {
      background-color: #f5f5f5;
      background-image: linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.5) 50%, transparent 70%);
      background-size: 200% 200%;
      animation: ctc-shimmer 1.5s infinite;
    }
    
    @keyframes ctc-shimmer {
      0% { background-position: -200% -200%; }
      100% { background-position: 200% 200%; }
    }
    
    [data-src].ctc-image-loaded, [data-bg-src].ctc-bg-loaded {
      animation: none;
      background-image: none;
    }
  `;
  
  document.head.appendChild(style);

})(window);
